import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import {
  HiOutlineSquares2X2,
  HiOutlineUserGroup,
  HiOutlineCurrencyDollar,
  HiOutlineBuildingOffice2,
  HiOutlineMagnifyingGlass,
  HiOutlineArrowPath,
  HiOutlineEye,
  HiOutlineKey,
  HiOutlineCalendarDays,
  HiOutlineChartBar,
  HiOutlineShieldCheck,
  HiOutlineUsers,
  HiOutlineClock
} from "react-icons/hi2";
import {
  dashboardGrid,
  cardTitle,
  cardSubtext,
  cardNumber
} from "../styles/ui";

const theme = {
  primary: "#0F766E",
  primaryDark: "#115E59",
  primaryLight: "#14B8A6",
  accent: "#2DD4BF",
  background: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceSoft: "#ECFEFF",
  text: "#0F172A",
  textMuted: "#64748B",
  border: "#D1FAE5",
  borderSoft: "#E2E8F0",
  success: "#16A34A",
  warning: "#F59E0B",
  danger: "#DC2626",
  info: "#0EA5E9"
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
  }).format(value || 0);
};

const getBarColor = (plan) => {
  if (plan === "Basic") return "#3B82F6";
  if (plan === "Pro") return "#10B981";
  if (plan === "Enterprise") return "#F59E0B";

  return "#64748B";
};

const PLAN_CONFIG = {
  Basic: {
    price: 199,
    users: 30,
    admins: 1
  },
  Pro: {
    price: 299,
    users: 100,
    admins: 1
  },
  Enterprise: {
    price: 499,
    users: 1000,
    admins: 2
  }
};

const DEFAULT_ADMIN_PASSWORD = "Admin@123";

function formatDateDisplay(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}

function formatDateISO(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const date = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${date}`;
}

function addMonths(baseDate, months) {
  const d = new Date(baseDate);
  if (Number.isNaN(d.getTime())) return "";
  d.setMonth(d.getMonth() + months);
  return formatDateISO(d);
}

function getTenureLabel(startDate, renewalDate) {
  if (!startDate || !renewalDate) return "-";
  const start = new Date(startDate);
  const end = new Date(renewalDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "-";

  const totalMonths =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth());

  if (totalMonths <= 3) return "Quarterly";
  if (totalMonths <= 12) return "Yearly";
  return `${totalMonths} Months`;
}

function getPlanLimit(plan) {
  return PLAN_CONFIG[plan]?.users || 30;
}

function StatusPill({ text, color, bg }) {
  return (
    <span
      style={{
        padding: "6px 10px",
        borderRadius: "999px",
        fontSize: "12px",
        fontWeight: 700,
        color,
        background: bg,
        display: "inline-flex",
        alignItems: "center",
        gap: "6px"
      }}
    >
      {text}
    </span>
  );
}

function MetricCard({ icon, label, value, sub, tone = "default" }) {
  const toneMap = {
    default: {
      bg: "#FFFFFF",
      iconBg: "#ECFEFF",
      iconColor: theme.primary
    },
    success: {
      bg: "#FFFFFF",
      iconBg: "#ECFDF5",
      iconColor: theme.success
    },
    warning: {
      bg: "#FFFFFF",
      iconBg: "#FFFBEB",
      iconColor: theme.warning
    },
    info: {
      bg: "#FFFFFF",
      iconBg: "#EFF6FF",
      iconColor: theme.info
    }
  };

  const t = toneMap[tone] || toneMap.default;

  return (
    <div style={{ ...metricCard, background: t.bg }}>
      <div style={{ ...metricIcon, background: t.iconBg, color: t.iconColor }}>
        {icon}
      </div>
      <div>
        <div style={metricLabel}>{label}</div>
        <div style={metricValue}>{value}</div>
        {sub ? <div style={metricSub}>{sub}</div> : null}
      </div>
    </div>
  );
}

const ProgressRow = ({ label, value, total }) => {
  const percent = total ? (value / total) * 100 : 0;

  return (
    <div style={{ marginBottom: "14px" }}>
      {/* Top Row */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "6px",
        fontSize: "13px",
        fontWeight: 600
      }}>
        <span>{label}</span>

        <span style={{ color: "#475569", fontWeight: 500 }}>
          {formatCurrency(value)} ({percent.toFixed(0)}%)
        </span>
      </div>

      {/* Progress Bar */}
      <div style={{
        height: "10px",
        background: "#E2E8F0",
        borderRadius: "999px",
        overflow: "hidden"
      }}>
        <div style={{
          width: `${percent}%`,
          height: "100%",
          background: getBarColor(label),
          borderRadius: "999px",
          transition: "width 0.4s ease"
        }} />
      </div>
    </div>
  );
};

function SuperAdminPanel({ activeTab, setActiveTab }) {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCompanyLoading, setSelectedCompanyLoading] = useState(false);
  const [selectedCompanyProfile, setSelectedCompanyProfile] = useState(null);

  const [createMessage, setCreateMessage] = useState("");
  const [createLoading, setCreateLoading] = useState(false);

  const todayISO = formatDateISO(new Date());

  const [form, setForm] = useState({
    company_name: "",
    plan: "Basic",
    payment_mode: "Yearly",
    start_date: todayISO,
    admin_email: ""
  });

  const renewalPreview = useMemo(() => {
  if (!form.start_date) return "";
  return addMonths(form.start_date, 12);
}, [form.start_date]);

  const planUsersPreview = useMemo(() => {
    return getPlanLimit(form.plan);
  }, [form.plan]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const res = await api.get("/superadmin/companies");
      setCompanies(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Super admin companies load failed:", err);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  loadCompanies(); // initial load

  const interval = setInterval(() => {
    loadCompanies(); // auto refresh
  }, 120000); // every 120 seconds

  return () => clearInterval(interval); // cleanup
}, []);

  const openCompanyProfile = async (company) => {
    try {
      setSelectedCompany(company);
      setSelectedCompanyLoading(true);
      setSelectedCompanyProfile(null);

      const res = await api.get(`/superadmin/company/${company.id}`);
      setSelectedCompanyProfile(res.data || null);
    } catch (err) {
      console.error("Company profile load failed:", err);
      setSelectedCompanyProfile({
        company,
        users: 0
      });
    } finally {
      setSelectedCompanyLoading(false);
    }
  };

  const closeCompanyProfile = () => {
    setSelectedCompany(null);
    setSelectedCompanyProfile(null);
    setSelectedCompanyLoading(false);
  };

  const createCompany = async () => {
    console.log("create company");
    setCreateMessage("");

    if (!String(form.company_name || "").trim()) {
      setCreateMessage("❌ Company name is required");
      return;
    }

    if (!String(form.admin_email || "").trim()) {
      setCreateMessage("❌ Admin email is required");
      return;
    }

    if (!String(form.start_date || "").trim()) {
      setCreateMessage("❌ Subscription start date is required");
      return;
    }

    try {
      setCreateLoading(true);

const res = await api.post("/superadmin/create-company", {
  company_name: form.company_name.trim(),
  plan: form.plan,
  payment_mode: form.payment_mode,
  start_date: form.start_date,
  admin_email: form.admin_email.trim()
});

      setCreateMessage(
        `✅ Company created successfully${res.data?.company_code ? ` (${res.data.company_code})` : ""}`
      );

      setForm({
        company_name: "",
        plan: "Basic",
        payment_mode: "Yearly",
        start_date: todayISO,
        admin_email: ""
      });

      await loadCompanies();
      setActiveTab("dashboard");
    }catch (err) {
  console.log(err.response?.data);

  setCreateMessage(
    err.response?.data?.error ||
    JSON.stringify(err.response?.data) ||
    "❌ Failed to create company"
  );
} finally {
      setCreateLoading(false);
    }
  };

  const resetAdminPassword = async (email) => {
    const newPassword = window.prompt(`Enter new password for ${email}`);
    if (!newPassword || !String(newPassword).trim()) return;

    try {
      await api.post("/superadmin/reset-admin-password", {
        email,
        password: String(newPassword).trim()
      });

      alert("Admin password reset successfully");
      if (selectedCompany) {
        await openCompanyProfile(selectedCompany);
      } else {
        await loadCompanies();
      }
    } catch (err) {
      alert(err.response?.data?.error || "Failed to reset password");
    }
  };

  const filteredCompanies = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return companies;

    return companies.filter((company) => {
      return [
        company.company_name,
        company.company_code,
        company.plan,
        company.admin_email
      ]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [companies, search]);

  const totalCompanies = companies.length;
  const activeSubscriptions = companies.filter((c) => {
    if (!c.renewal_date) return false;
    return new Date(c.renewal_date) >= new Date(new Date().setHours(0, 0, 0, 0));
  }).length;
  const expiringSoon = companies.filter((c) => {
    if (!c.renewal_date) return false;
    const renewal = new Date(c.renewal_date);
    const today = new Date();
    const diffDays = Math.ceil((renewal - today) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 30;
  }).length;

  const totalSeats = companies.reduce(
    (sum, c) => sum + Number(c.plan_users || getPlanLimit(c.plan)),
    0
  );

  const usedSeats = companies.reduce((sum, c) => {
    const guessedUsers =
      Number(c.users_created || c.user_count || c.current_users || 0);
    return sum + guessedUsers;
  }, 0);

  const planCounts = useMemo(() => {
    return {
      Basic: companies.filter((c) => c.plan === "Basic").length,
      Pro: companies.filter((c) => c.plan === "Pro").length,
      Enterprise: companies.filter((c) => c.plan === "Enterprise").length
    };
  }, [companies]);

  const revenueStats = useMemo(() => {
  let totalRevenue = 0;
  let monthlyRevenue = 0;

  let breakdown = {
    Basic: 0,
    Pro: 0,
    Enterprise: 0
  };

  companies.forEach((company) => {
    const plan = company.plan || "Basic";
    const mode = company.payment_mode || "Yearly";

    let yearlyPrice = PLAN_CONFIG[plan]?.price || 0;

    let revenue = yearlyPrice;

    if (mode === "Quarterly") revenue = yearlyPrice / 4;
    if (mode === "Monthly") revenue = yearlyPrice / 12;

    totalRevenue += revenue;
    monthlyRevenue += yearlyPrice / 12;

    breakdown[plan] += revenue;
  });

  
  return {
    totalRevenue,
    monthlyRevenue,
    yearlyRevenue: monthlyRevenue * 12,
    breakdown
  };
}, [companies]);

  const topCompaniesByCapacity = useMemo(() => {
    return [...companies]
      .sort(
        (a, b) =>
          Number(b.plan_users || getPlanLimit(b.plan)) -
          Number(a.plan_users || getPlanLimit(a.plan))
      )
      .slice(0, 5);
  }, [companies]);

  const dashboardContent = (
  <div style={dashboardWrapper}>
      <div style={{ ...topStatsGrid, marginBottom: "28px" }}>
        <MetricCard
          icon={<HiOutlineBuildingOffice2 />}
          label="Total Companies"
          value={totalCompanies}
          sub="Subscribed organizations"
        />
        <MetricCard
          icon={<HiOutlineShieldCheck />}
          label="Active Subscriptions"
          value={activeSubscriptions}
          sub="Currently valid"
          tone="success"
        />
        <MetricCard
          icon={<HiOutlineClock />}
          label="Expiring in 30 Days"
          value={expiringSoon}
          sub="Need follow-up soon"
          tone="warning"
        />
        <MetricCard
          icon={<HiOutlineUsers />}
          label="Plan Seats"
          value={totalSeats}
          sub={`Used seats tracked: ${usedSeats}`}
          tone="info"
        />
      </div>

      <div style={{ ...dashboardGrid, marginBottom: "28px" }}>
        <div style={panel}>
          <div style={panelHeader}>
            <div style={panelTitleWrap}>
              <div style={panelIcon}>
                <HiOutlineChartBar />
              </div>
              <div>
                <h3 style={cardTitle}>Plan Distribution</h3>
<p style={cardSubtext}>Company count by subscription plan</p>
              </div>
            </div>
          </div>

         
        <div style={planBoxGrid}>
  <div style={planBox}>
    <div style={planLabel}>Basic</div>
    <div style={planValue}>{planCounts.Basic}</div>
  </div>

  <div style={planBox}>
    <div style={planLabel}>Pro</div>
    <div style={planValue}>{planCounts.Pro}</div>
  </div>

  <div style={planBox}>
    <div style={planLabel}>Enterprise</div>
    <div style={planValue}>{planCounts.Enterprise}</div>
  </div>
</div> </div>

        <div style={panel}>
          <div style={panelHeader}>
            <div style={panelTitleWrap}>
              <div style={panelIcon}>
                <HiOutlineShieldCheck />
              </div>
              <div>
                <h3 style={cardTitle}>Subscription Health</h3>
                <p style={cardSubtext}>Quick status split</p>
              </div>
            </div>
          </div>

          <div style={healthGrid}>
            <div style={healthCard}>
              <div style={healthLabel}>Active</div>
              <div style={{ ...healthValue, color: theme.success }}>
                {activeSubscriptions}
              </div>
            </div>
            <div style={healthCard}>
              <div style={healthLabel}>Expiring</div>
              <div style={{ ...healthValue, color: theme.warning }}>
                {expiringSoon}
              </div>
            </div>
            <div style={healthCard}>
              <div style={healthLabel}>Expired</div>
              <div style={{ ...healthValue, color: theme.danger }}>
                {Math.max(totalCompanies - activeSubscriptions, 0)}
              </div>
            </div>
          </div>
        </div>

        <div style={panel}>
          <div style={panelHeader}>
            <div style={panelTitleWrap}>
              <div style={panelIcon}>
                <HiOutlineUsers />
              </div>
              <div>
                <h3 style={panelTitle}>Seat Utilization</h3>
                <p style={panelSubtitle}>Users created versus total plan seats</p>
              </div>
            </div>
          </div>

          <div style={bigUtilWrap}>
            <div style={bigUtilNumber}>{usedSeats}</div>
            <div style={bigUtilSub}>Used out of {totalSeats || 0} total seats</div>

            <div style={bigTrack}>
              <div
                style={{
                  ...bigFill,
                  width: `${totalSeats > 0 ? Math.min((usedSeats / totalSeats) * 100, 100) : 0}%`
                }}
              />
            </div>
          </div>
        </div>

        <div style={panel}>
          <div style={panelHeader}>
            <div style={panelTitleWrap}>
              <div style={panelIcon}>
                <HiOutlineCurrencyDollar />
              </div>
              <div>
                <h3 style={panelTitle}>Revenue Snapshot</h3>
                <p style={panelSubtitle}>Live subscription revenue overview</p>
              </div>
            </div>
          </div>

          <div style={revenueBox}>
            <div style={revenueValue}>{formatCurrency(revenueStats.totalRevenue)}</div>
            <div style={revenueSub}>
              Revenue is calculated automatically based on active company subscriptions and selected plans.
            </div>
          </div>
        </div>
      </div>

      <div style={bottomGrid}>
        <div style={panel}>
          <div style={panelHeaderRow}>
            <div style={panelTitleWrap}>
              <div style={panelIcon}>
                <HiOutlineBuildingOffice2 />
              </div>
              <div>
                <h3 style={panelTitle}>Companies</h3>
                <p style={panelSubtitle}>Search and manage subscribed companies</p>
              </div>
            </div>

            <div style={searchWrap}>
              <HiOutlineMagnifyingGlass style={searchIcon} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search company..."
                style={searchInput}
              />
            </div>
          </div>

          {loading ? (
            <div style={emptyState}>Loading companies...</div>
          ) : filteredCompanies.length === 0 ? (
            <div style={emptyState}>No companies found</div>
          ) : (
            <div style={companyList}>
              {filteredCompanies.map((company) => {
                const allowedUsers = Number(company.plan_users || getPlanLimit(company.plan));
                const currentUsers = Number(
                  company.users_created || company.user_count || company.current_users || 0
                );
                const active =
                  company.renewal_date &&
                  new Date(company.renewal_date) >= new Date(new Date().setHours(0, 0, 0, 0));

                return (
                  <button
                    key={company.id}
                    type="button"
                    onClick={() => openCompanyProfile(company)}
                    style={companyCard}
                  >
                    <div style={companyCardTop}>
                      <div>
                        <div style={companyName}>{company.company_name || "-"}</div>
                        <div style={companyMeta}>
                          {company.company_code || "-"} • {company.plan || "-"} •{" "}
                        </div>
                      </div>

                      <StatusPill
                        text={active ? "Active" : "Expired"}
                        color={active ? theme.success : theme.danger}
                        bg={active ? "#DCFCE7" : "#FEE2E2"}
                      />
                    </div>

                    <div style={companyCardGrid}>
                      <div style={smallInfo}>
                        <span style={smallInfoLabel}>Start</span>
                        <span style={smallInfoValue}>{formatDateDisplay(company.start_date)}</span>
                      </div>
                      <div style={smallInfo}>
                        <span style={smallInfoLabel}>Renewal</span>
                        <span style={smallInfoValue}>{formatDateDisplay(company.renewal_date)}</span>
                      </div>
                      <div style={smallInfo}>
                        <span style={smallInfoLabel}>Users</span>
                        <span style={smallInfoValue}>
                          {currentUsers} / {allowedUsers}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={panel}>
          <div style={panelHeader}>
            <div style={panelTitleWrap}>
              <div style={panelIcon}>
                <HiOutlineUserGroup />
              </div>
              <div>
                <h3 style={panelTitle}>Top Companies by Seat Capacity</h3>
                <p style={panelSubtitle}>Companies with highest seat capacity</p>
              </div>
            </div>
          </div>

          {topCompaniesByCapacity.length === 0 ? (
            <div style={emptyState}>No companies available</div>
          ) : (
            <div style={rankList}>
              {topCompaniesByCapacity.map((company, index) => (
                <div key={company.id} style={rankRow}>
                  <div style={rankBadge}>{index + 1}</div>
                  <div style={{ flex: 1 }}>
                    <div style={rankTitle}>{company.company_name || "-"}</div>
                    <div style={rankSub}>
                      {company.plan || "-"} • {company.company_code || "-"}
                    </div>
                  </div>
                  <div style={rankValue}>
                    {Number(company.plan_users || getPlanLimit(company.plan))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>  );

  const createUsersContent = (
    <div style={createLayout}>
      <div style={panel}>
        <div style={panelHeader}>
          <div style={panelTitleWrap}>
            <div style={panelIcon}>
              <HiOutlineUserGroup />
            </div>
            <div>
              <h3 style={panelTitle}>Create Company / Admin</h3>
              <p style={panelSubtitle}>
                Create a company subscription and assign an admin account.
              </p>
            </div>
          </div>
        </div>

        <div style={formGrid}>
          <div style={field}>
            <label style={label}>Company ID</label>
            <input
              value="Auto-generated on submit"
              readOnly
              style={{ ...input, background: "#F8FAFC", color: theme.textMuted }}
            />
          </div>

          <div style={field}>
            <label style={label}>Company Full Name</label>
            <input
              value={form.company_name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, company_name: e.target.value }))
              }
              placeholder="Enter company name"
              style={input}
            />
          </div>

          <div style={field}>
            <label style={label}>Plan</label>
            <select
              value={form.plan}
              onChange={(e) => setForm((prev) => ({ ...prev, plan: e.target.value }))}
              style={input}
            >
              <option value="Basic">Basic</option>
              <option value="Pro">Pro</option>
              <option value="Enterprise">Enterprise</option>
            </select>
          </div>

<div style={field}>
  <label style={label}>Payment Mode</label>

  <select
    value={form.payment_mode}
    onChange={(e) =>
      setForm((prev) => ({
        ...prev,
        payment_mode: e.target.value
      }))
    }
    style={input}
  >
    <option value="Yearly">Yearly</option>
    <option value="Quarterly">Quarterly</option>
  </select>
</div>

          <div style={field}>
            <label style={label}>Subscription Start Date</label>
            <input
              type="date"
              value={form.start_date}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, start_date: e.target.value }))
              }
              style={input}
            />
          </div>

          <div style={field}>
            <label style={label}>Renewal Date</label>
            <input
              value={renewalPreview}
              readOnly
              style={{ ...input, background: "#F8FAFC", color: theme.textMuted }}
            />
          </div>

          <div style={field}>
            <label style={label}>Plan User Limit</label>
            <input
              value={planUsersPreview}
              readOnly
              style={{ ...input, background: "#F8FAFC", color: theme.textMuted }}
            />
          </div>

          <div style={field}>
            <label style={label}>Admin Email</label>
            <input
              value={form.admin_email}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, admin_email: e.target.value }))
              }
              placeholder="Enter admin email"
              style={input}
            />
          </div>

          <div style={fieldFull}>
            <label style={label}>Default Admin Password</label>
            <div style={credentialBox}>
              <div style={credentialRow}>
                <HiOutlineKey style={{ fontSize: 18, color: theme.primary }} />
                <span style={credentialValue}>{DEFAULT_ADMIN_PASSWORD}</span>
              </div>
              <div style={credentialHelp}>
                On first login, the admin should be forced to change this password.
              </div>
            </div>
          </div>
        </div>

        <div style={{ ...buttonRow, gridColumn: "1 / -1" }}>
          <button
            type="button"
            onClick={createCompany}
            disabled={createLoading}
            style={{
              ...primaryBtn,
              opacity: createLoading ? 0.75 : 1,
              cursor: createLoading ? "not-allowed" : "pointer"
            }}
          >
            {createLoading ? "Creating..." : "Create Company"}
          </button>

          <button
            type="button"
            onClick={() => {
              setForm({
                company_name: "",
                plan: "Basic",
                payment_mode: "Yearly",
                start_date: todayISO,
                admin_email: ""
              });
              setCreateMessage("");
            }}
            style={secondaryBtn}
          >
            Reset
          </button>
        </div>

        {createMessage && (
          <div
            style={{
              ...messageBox,
              background: createMessage.includes("✅") ? "#ECFDF5" : "#FEF2F2",
              color: createMessage.includes("✅") ? theme.success : theme.danger,
              borderColor: createMessage.includes("✅") ? "#BBF7D0" : "#FECACA"
            }}
          >
            {createMessage}
          </div>
        )}
      </div>

      <div style={panel}>
        <div style={panelHeader}>
          <div style={panelTitleWrap}>
            <div style={panelIcon}>
              <HiOutlineEye />
            </div>
            <div>
              <h3 style={panelTitle}>Preview</h3>
              <p style={panelSubtitle}>How the subscription will be created</p>
            </div>
          </div>
        </div>

        <div style={previewStack}>
          <div style={previewCard}>
            <div style={previewLabel}>Plan</div>
            <div style={previewValue}>{form.plan}</div>
          </div>
          
      
          <div style={previewCard}>
            <div style={previewLabel}>Start Date</div>
            <div style={previewValue}>{formatDateDisplay(form.start_date)}</div>
          </div>
          <div style={previewCard}>
            <div style={previewLabel}>Renewal Date</div>
            <div style={previewValue}>{formatDateDisplay(renewalPreview)}</div>
          </div>
          <div style={previewCard}>
            <div style={previewLabel}>Tenure</div>
            <div style={previewValue}>
              {getTenureLabel(form.start_date, renewalPreview)}
            </div>
          </div>
          <div style={previewCard}>
            <div style={previewLabel}>Allowed Users</div>
            <div style={previewValue}>{planUsersPreview}</div>
          </div>
        </div>
      </div>
    </div>
  );

  const revenueContent = (
  <div style={panel}>
    <div style={panelHeader}>
      <div style={panelTitleWrap}>
        <div style={panelIcon}>
          <HiOutlineCurrencyDollar />
        </div>
        <div>
          <h3 style={panelTitle}>Revenue</h3>
          <p style={panelSubtitle}>
            Track your subscription revenue, growth, and billing insights across all companies.
          </p>
        </div>
      </div>
    </div>

    {/* ===== TOP METRICS ===== */}
    <div style={revenuePlaceholderGrid}>
      <MetricCard
        icon={<HiOutlineCurrencyDollar />}
        label="Current Revenue"
        value={formatCurrency(revenueStats.totalRevenue)}
        sub="Active billing snapshot"
      />

      <MetricCard
        icon={<HiOutlineChartBar />}
        label="Monthly Revenue (MRR)"
        value={formatCurrency(revenueStats.monthlyRevenue)}
        sub="Recurring monthly income"
        tone="info"
      />

      <MetricCard
        icon={<HiOutlineCalendarDays />}
        label="Yearly Revenue (ARR)"
        value={formatCurrency(revenueStats.yearlyRevenue)}
        sub="Projected yearly income"
        tone="success"
      />
    </div>

    <div style={revenueBottomGrid}>

  {/* QUICK INSIGHTS */}
  <div style={quickInsights}>
    <h4 style={sectionTitle}>Quick Insights</h4>

    <div style={insightItem}>
      <span>Total Companies</span>
      <strong>{companies.length}</strong>
    </div>

    <div style={insightItem}>
      <span>Avg Revenue</span>
      <strong>
        {formatCurrency(
          revenueStats.totalRevenue / (companies.length || 1)
        )}
      </strong>
    </div>

    <div style={insightItem}>
      <span>Active Plans</span>
      <strong>
        {
          Object.keys(revenueStats.breakdown).filter(
            (p) => revenueStats.breakdown[p] > 0
          ).length
        }
      </strong>
    </div>
  </div>

  {/* PLAN DISTRIBUTION */}
  <div style={planCard}>
    <h4 style={sectionTitle}>Revenue by Plan</h4>

    <ProgressRow
      label="Basic"
      value={revenueStats.breakdown.Basic}
      total={revenueStats.totalRevenue}
    />

    <ProgressRow
      label="Pro"
      value={revenueStats.breakdown.Pro}
      total={revenueStats.totalRevenue}
    />

    <ProgressRow
      label="Enterprise"
      value={revenueStats.breakdown.Enterprise}
      total={revenueStats.totalRevenue}
    />
  </div>

</div>
  </div>
);

  const companyData = selectedCompanyProfile?.company || selectedCompany;
  const companyUsedUsers = Number(
    selectedCompanyProfile?.users ||
      companyData?.users_created ||
      companyData?.user_count ||
      companyData?.current_users ||
      0
  );
  const companyAllowedUsers = getPlanLimit(companyData?.plan);

  return (
    <div style={page}>
      <div style={hero}>
        <div>
          <h2 style={title}>Super Admin Portal</h2>
          <p style={subtitle}>
            Manage subscriptions, create company admins, monitor seats, and control company-level access.
          </p>
        </div>

        <button type="button" onClick={loadCompanies} style={refreshBtn}>
          <HiOutlineArrowPath style={{ fontSize: 16 }} />
          Refresh
        </button>
      </div>

      
      {activeTab === "dashboard" && dashboardContent}
      {activeTab === "create" && createUsersContent}
      {activeTab === "revenue" && revenueContent}

      {selectedCompany && (
        <div style={drawerOverlay} onClick={closeCompanyProfile}>
          <div style={drawer} onClick={(e) => e.stopPropagation()}>
            <div style={drawerHeader}>
              <div>
                <h3 style={drawerTitle}>Company Profile</h3>
                <p style={drawerSubtitle}>
                  Full subscription and admin details
                </p>
              </div>

              <button type="button" onClick={closeCompanyProfile} style={drawerCloseBtn}>
                Close
              </button>
            </div>

            {selectedCompanyLoading ? (
              <div style={emptyState}>Loading company profile...</div>
            ) : !companyData ? (
              <div style={emptyState}>Unable to load company profile</div>
            ) : (
              <div style={drawerContent}>
                <div style={drawerGrid}>
                  <div style={infoCard}>
                    <div style={infoLabel}>Company Full Name</div>
                    <div style={infoValue}>{companyData.company_name || "-"}</div>
                  </div>

                  <div style={infoCard}>
                    <div style={infoLabel}>Company ID</div>
                    <div style={infoValue}>{companyData.company_code || "-"}</div>
                  </div>

                  <div style={infoCard}>
                    <div style={infoLabel}>Plan</div>
                    <div style={infoValue}>{companyData.plan || "-"}</div>
                  </div>


                  <div style={infoCard}>
                    <div style={infoLabel}>Subscription Date</div>
                    <div style={infoValue}>{formatDateDisplay(companyData.start_date)}</div>
                  </div>

                  <div style={infoCard}>
                    <div style={infoLabel}>Renewal Date</div>
                    <div style={infoValue}>{formatDateDisplay(companyData.renewal_date)}</div>
                  </div>

                  <div style={infoCard}>
                    <div style={infoLabel}>Plan Tenure</div>
                    <div style={infoValue}>
                      {getTenureLabel(companyData.start_date, companyData.renewal_date)}
                    </div>
                  </div>

                  <div style={infoCard}>
                    <div style={infoLabel}>Users Created</div>
                    <div style={infoValue}>
                      {companyUsedUsers} / {companyAllowedUsers}
                    </div>
                  </div>

                  <div style={infoCard}>
                    <div style={infoLabel}>Admin Email</div>
                    <div style={infoValue}>{companyData.admin_email || "-"}</div>
                  </div>

<div style={infoCard}>
  <div style={infoLabel}>Admin Password</div>
  <div style={infoValue}>
    Default password assigned securely
  </div>
</div>
                </div>

                <div style={seatPanel}>
                  <div style={seatHeader}>
                    <HiOutlineUsers style={{ fontSize: 18, color: theme.primary }} />
                    <span style={seatTitle}>Seat Utilization</span>
                  </div>

                  <div style={seatCount}>
                    {companyUsedUsers} / {companyAllowedUsers}
                  </div>

                  <div style={seatTrack}>
                    <div
                      style={{
                        ...seatFill,
                        width: `${
                          companyAllowedUsers > 0
                            ? Math.min((companyUsedUsers / companyAllowedUsers) * 100, 100)
                            : 0
                        }%`
                      }}
                    />
                  </div>
                </div>

                <div style={drawerActionRow}>
                  <button
                    type="button"
                    onClick={() => resetAdminPassword(companyData.admin_email)}
                    style={primaryBtn}
                  >
                    <HiOutlineKey style={{ fontSize: 16 }} />
                    Reset Admin Password
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ================= STYLES ================= */

const page = {
  fontFamily: "sans-serif",
  color: theme.text
};

const hero = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
  marginBottom: "24px",
  flexWrap: "wrap"
};

const title = {
  margin: 0,
  fontSize: "28px",
  fontWeight: 800,
  color: theme.text
};

const subtitle = {
  margin: "8px 0 0 0",
  color: theme.textMuted,
  fontSize: "14px",
  lineHeight: 1.6,
  maxWidth: "820px"
};

const refreshBtn = {
  border: "none",
  background: theme.primary,
  color: "#fff",
  padding: "12px 16px",
  borderRadius: "14px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  fontWeight: 700,
  boxShadow: "0 10px 24px rgba(15,118,110,0.16)"
};

const tabRow = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
  marginBottom: "22px"
};

const tabBtn = {
  border: "1px solid #D1FAE5",
  background: "#FFFFFF",
  color: theme.primaryDark,
  padding: "11px 16px",
  borderRadius: "14px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  fontWeight: 700
};

const activeTabBtn = {
  background: "linear-gradient(135deg, #0F766E, #14B8A6)",
  color: "#FFFFFF",
  border: "1px solid transparent",
  boxShadow: "0 12px 24px rgba(15,118,110,0.18)"
};

const topStatsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: "20px",
  marginBottom: "24px"
};

const metricCard = {
  borderRadius: "20px",
  border: "1px solid #E6FFFB",
  padding: "18px",
  display: "flex",
  gap: "14px",
  alignItems: "flex-start",
  boxShadow: "0 10px 28px rgba(15,23,42,0.06)"
};

const metricIcon = {
  width: "46px",
  height: "46px",
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "22px",
  flexShrink: 0
};

const metricLabel = {
  fontSize: "13px",
  color: theme.textMuted,
  fontWeight: 700,
  marginBottom: "6px"
};

const metricValue = {
  fontSize: "26px",
  fontWeight: 800,
  color: theme.text,
  lineHeight: 1.2
};

const metricSub = {
  marginTop: "8px",
  fontSize: "12px",
  color: theme.textMuted
};


const bottomGrid = {
  display: "grid",
  gridTemplateColumns: "1.5fr 1fr",
  gap: "20px"
};

const panel = {
  background: "#FFFFFF",
  borderRadius: "18px",
  padding: "20px",
  border: "1px solid #E6FFFB",
  boxShadow: "0 8px 20px rgba(15,23,42,0.05)"
};

const panelHeader = {
  marginBottom: "16px"
};

const panelHeaderRow = {
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  alignItems: "center",
  marginBottom: "18px",
  flexWrap: "wrap"
};

const panelTitleWrap = {
  display: "flex",
  gap: "12px",
  alignItems: "center"
};

const panelIcon = {
  width: "42px",
  height: "42px",
  borderRadius: "14px",
  background: "#ECFEFF",
  color: theme.primary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  flexShrink: 0
};

const panelTitle = {
  margin: 0,
  fontSize: "16px",
  fontWeight: 700,
  color: theme.text,
  lineHeight: 1.3
};

const panelSubtitle = {
  margin: "6px 0 0 0",
  color: theme.textMuted,
  fontSize: "12.5px",
  lineHeight: 1.6
};

const chartStack = {
  display: "flex",
  flexDirection: "column",
  gap: "14px"
};

const progressRow = {
  display: "flex",
  flexDirection: "column",
  gap: "8px"
};

const progressTop = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  fontSize: "13px"
};

const progressLabel = {
  color: theme.text,
  fontWeight: 700
};

const progressCount = {
  color: theme.textMuted,
  fontWeight: 700
};

const progressTrack = {
  height: "10px",
  borderRadius: "999px",
  background: "#EDF2F7",
  overflow: "hidden"
};

const healthGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "16px",
  marginTop: "14px"
};

const healthCard = {
  background: "#F8FAFC",
  borderRadius: "16px",
  padding: "14px",
  border: "1px solid #EEF2F7",

  display: "flex",
  flexDirection: "column",
  justifyContent: "center",   // vertical alignment
  alignItems: "center",       // horizontal alignment
  minHeight: "90px"           // ensures equal height
};

const healthLabel = {
  fontSize: "13px",
  color: theme.textMuted,
  fontWeight: 700,
  marginBottom: "8px"
};

const healthValue = {
  fontSize: "26px",
  fontWeight: 800
};

const bigUtilWrap = {
  display: "flex",
  flexDirection: "column",
  gap: "10px"
};

const bigUtilNumber = {
  fontSize: "30px",
  fontWeight: 800,
  color: theme.text,
  lineHeight: 1.2
};

const bigUtilSub = {
  color: theme.textMuted,
  fontSize: "13px"
};

const bigTrack = {
  height: "14px",
  borderRadius: "999px",
  background: "#E5E7EB",
  overflow: "hidden",
  marginTop: "8px"
};

const bigFill = {
  height: "100%",
  background: "linear-gradient(90deg, #0F766E, #14B8A6)",
  borderRadius: "999px"
};

const revenueBox = {
  display: "flex",
  flexDirection: "column",
  gap: "10px"
};

const revenueValue = {
  fontSize: "30px",
  fontWeight: 800,
  color: theme.text,
  lineHeight: 1.2
};

const revenueSub = {
  fontSize: "13px",
  color: theme.textMuted,
  lineHeight: 1.7
};

const searchWrap = {
  position: "relative",
  minWidth: "260px"
};

const searchIcon = {
  position: "absolute",
  left: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  color: theme.textMuted,
  fontSize: "16px"
};

const searchInput = {
  width: "100%",
  padding: "11px 14px 11px 38px",
  borderRadius: "14px",
  border: "1px solid #D1FAE5",
  outline: "none",
  fontSize: "14px",
  boxSizing: "border-box"
};

const companyList = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  maxHeight: "560px",
  overflowY: "auto",
  paddingRight: "4px"
};

const companyCard = {
  width: "100%",
  border: "1px solid #E2E8F0",
  background: "#FFFFFF",
  borderRadius: "18px",
  padding: "16px",
  textAlign: "left",
  cursor: "pointer",
  boxShadow: "0 8px 18px rgba(15,23,42,0.04)"
};

const companyCardTop = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  alignItems: "flex-start",
  marginBottom: "14px"
};

const companyName = {
  fontSize: "16px",
  fontWeight: 800,
  color: theme.text,
  marginBottom: "4px"
};

const companyMeta = {
  fontSize: "12px",
  color: theme.textMuted,
  lineHeight: 1.5
};

const companyCardGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "10px"
};

const smallInfo = {
  background: "#F8FAFC",
  borderRadius: "14px",
  padding: "12px",
  border: "1px solid #EEF2F7"
};

const smallInfoLabel = {
  display: "block",
  fontSize: "11px",
  color: theme.textMuted,
  fontWeight: 700,
  marginBottom: "6px"
};

const smallInfoValue = {
  display: "block",
  fontSize: "13px",
  color: theme.text,
  fontWeight: 700
};

const rankList = {
  display: "flex",
  flexDirection: "column",
  gap: "10px"
};

const rankRow = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  background: "#F8FAFC",
  borderRadius: "16px",
  padding: "12px 14px",
  border: "1px solid #EEF2F7"
};

const rankBadge = {
  width: "30px",
  height: "30px",
  borderRadius: "50%",
  background: "#ECFEFF",
  color: theme.primaryDark,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 800,
  fontSize: "13px",
  flexShrink: 0
};

const rankTitle = {
  fontSize: "14px",
  fontWeight: 800,
  color: theme.text
};

const rankSub = {
  fontSize: "12px",
  color: theme.textMuted,
  marginTop: "2px"
};

const rankValue = {
  fontSize: "18px",
  fontWeight: 800,
  color: theme.primaryDark
};

const emptyState = {
  padding: "30px 10px",
  color: theme.textMuted,
  textAlign: "center",
  fontWeight: 600
};

const createLayout = {
  display: "grid",
  gridTemplateColumns: "1.2fr 0.8fr",
  gap: "18px"
};


const field = {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  minWidth: 0              // ✅ prevents overflow pushing
};

const fieldFull = {
  gridColumn: "1 / -1",
  display: "flex",
  flexDirection: "column",
  gap: "8px"
};

const label = {
  fontSize: "13px",
  fontWeight: 700,
  color: theme.text
};

const input = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: "10px",
  border: "1px solid #E2E8F0",
  fontSize: "13px",
  outline: "none",
  background: "#FFFFFF",
  height: "42px",          // ✅ IMPORTANT
  boxSizing: "border-box"
};

const credentialBox = {
  border: "1px solid #CCFBF1",
  background: "#ECFEFF",
  borderRadius: "16px",
  padding: "14px"
};

const credentialRow = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginBottom: "8px"
};

const credentialValue = {
  fontSize: "15px",
  fontWeight: 800,
  color: theme.primaryDark
};

const credentialHelp = {
  fontSize: "12px",
  color: theme.textMuted,
  lineHeight: 1.6
};

const buttonRow = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: "12px",
  marginTop: "20px",
  width: "100%",
  flexWrap: "wrap"
};

const primaryBtn = {
  border: "none",
  background: theme.primary,
  color: "#fff",
  padding: "12px 18px",
  borderRadius: "14px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  fontWeight: 700,
  boxShadow: "0 10px 24px rgba(15,118,110,0.14)",
  width: "auto",           // ⭐ ADD THIS
  flexShrink: 0            // ⭐ ADD THIS
};

const secondaryBtn = {
  border: "1px solid #D1FAE5",
  background: "#FFFFFF",
  color: theme.primaryDark,
  padding: "12px 18px",
  borderRadius: "14px",
  cursor: "pointer",
  minWidth: "120px",
  flexShrink: 0,
  fontWeight: 700,
  width: "auto"            // ⭐ ADD THIS
};

const messageBox = {
  marginTop: "14px",
  padding: "12px 14px",
  borderRadius: "14px",
  border: "1px solid transparent",
  fontWeight: 700,
  fontSize: "13px"
};

const previewStack = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "12px"
};

const previewCard = {
  background: "#F8FAFC",
  border: "1px solid #EEF2F7",
  borderRadius: "16px",
  padding: "14px"
};

const previewLabel = {
  fontSize: "12px",
  fontWeight: 700,
  color: theme.textMuted,
  marginBottom: "6px"
};

const previewValue = {
  fontSize: "15px",
  fontWeight: 800,
  color: theme.text
};

const revenuePlaceholderGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "16px",
  marginBottom: "20px"
};


const drawerOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.38)",
  zIndex: 1200,
  display: "flex",
  justifyContent: "flex-end"
};

const drawer = {
  width: "100%",
  maxWidth: "720px",
  height: "100vh",
  background: "#FFFFFF",
  boxShadow: "-20px 0 40px rgba(15,23,42,0.16)",
  padding: "24px",
  boxSizing: "border-box",
  overflowY: "auto"
};

const drawerHeader = {
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  alignItems: "flex-start",
  marginBottom: "20px"
};

const drawerTitle = {
  margin: 0,
  fontSize: "24px",
  fontWeight: 800,
  color: theme.text
};

const drawerSubtitle = {
  margin: "6px 0 0 0",
  fontSize: "13px",
  color: theme.textMuted
};

const drawerCloseBtn = {
  border: "1px solid #D1FAE5",
  background: "#FFFFFF",
  color: theme.primaryDark,
  padding: "10px 14px",
  borderRadius: "12px",
  cursor: "pointer",
  fontWeight: 700
};

const drawerContent = {
  display: "flex",
  flexDirection: "column",
  gap: "18px"
};

const drawerGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "14px"
};

const infoCard = {
  border: "1px solid #EEF2F7",
  background: "#F8FAFC",
  borderRadius: "16px",
  padding: "14px"
};

const infoLabel = {
  fontSize: "12px",
  fontWeight: 700,
  color: theme.textMuted,
  marginBottom: "6px"
};

const infoValue = {
  fontSize: "15px",
  fontWeight: 800,
  color: theme.text,
  lineHeight: 1.5
};

const seatPanel = {
  border: "1px solid #CCFBF1",
  background: "#ECFEFF",
  borderRadius: "18px",
  padding: "18px"
};

const seatHeader = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "12px"
};

const seatTitle = {
  fontSize: "14px",
  fontWeight: 800,
  color: theme.primaryDark
};

const seatCount = {
  fontSize: "28px",
  fontWeight: 800,
  color: theme.text,
  marginBottom: "12px"
};

const seatTrack = {
  height: "14px",
  borderRadius: "999px",
  background: "#D1FAE5",
  overflow: "hidden"
};

const seatFill = {
  height: "100%",
  borderRadius: "999px",
  background: "linear-gradient(90deg, #0F766E, #14B8A6)"
};

const drawerActionRow = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap"

};
const dashboardWrapper = {
  background: "#F8FAFC",
  padding: "20px",
  borderRadius: "20px"
};


const formGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "18px",
  marginTop: "14px",
  alignItems: "start",
  columnGap: "18px",
  rowGap: "18px"
};

const planBoxGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "16px",
  marginTop: "16px"
};

const planBox = {
  background: "#F8FAFC",
  borderRadius: "16px",
  padding: "14px",
  border: "1px solid #EEF2F7",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "90px"
};

const planLabel = {
  fontSize: "13px",
  color: "#64748B",
  fontWeight: 700,
  marginBottom: "6px"
};

const planValue = {
  fontSize: "26px",
  fontWeight: 800,
  color: "#0F172A"
};

const revenueBottomGrid = {
  display: "grid",
  gridTemplateColumns: "320px 1fr",
  gap: "18px",
  marginTop: "20px",
  alignItems: "start"
};

const planCard = {
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  borderRadius: "16px",
  padding: "18px"
};

const quickInsights = {
  background: "#F8FAFC",
  border: "1px solid #E2E8F0",
  borderRadius: "16px",
  padding: "18px"
};

const sectionTitle = {
  margin: "0 0 14px 0",
  fontSize: "15px",
  fontWeight: 700,
  color: "#0F172A"
};

const insightItem = {
  display: "flex",
  justifyContent: "space-between",
  padding: "10px 0",
  borderBottom: "1px solid #E2E8F0",
  fontSize: "14px",
  color: "#475569"
};


export default SuperAdminPanel;
